import { some } from 'lodash';
import { default as React, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Card } from '../../components/Card/Card';
import { Grid } from '../../components/Grid/Grid';
import { IntervalField } from '../../components/IntervalField/IntervalField';
import { NewContainer } from '../../components/new_container/new_container';
import { NoDataDisplay } from '../../components/no_data_display/no_data_display';
import { Stack } from '../../components/Stack/Stack';
import { Text } from '../../components/text/text';
import { graphql } from '../../generated';
import { SalesOverTimePeriod } from '../../generated/graphql';
import { t } from '../../locales/translate';
import { useQueryHandler } from '../../model/graphql';
import { merchActivated, ticketingActivated, useSelectedMembership } from '../../model/membership';
import { useMeta } from '../../model/meta';
import { eventsPath, productsPath } from '../../routes';
import { beginningOfDay, earliestDate, endOfDay, roundedDatetimeToPastTenMins } from '../../util/datetime';
import './Dashboard.css';
import { AverageOrderValue } from './statistics/average_order_value';
import { Countries } from './statistics/countries';
import { OrdersPlaced } from './statistics/orders_placed';
import { PointsOfSale } from './statistics/points_of_sale';
import { Products } from './statistics/products';
import { Sessions } from './statistics/sessions';
import { TurnoverOverTime } from './statistics/turnover_over_time';

const DASHBOARD_PERMISSIONS = graphql(`
  query DashboardPermissions {
    statistics {
      permissions {
        id
        turnoverOverTime
        persistedSessionsOverTime
        countries
        pointsOfSale
        products
        averageOrderValueOverTime
        placedOrderCountOverTime
      }
    }
  }
`);

export function Dashboard(): JSX.Element {
  useMeta({ title: t.page.dashboard.title });
  const membership = useSelectedMembership();
  const [intervalValues, setIntervalValues] = useState<{ from: Date; to: Date; period: SalesOverTimePeriod }>(() => {
    const from = new Date();
    const to = new Date();
    from.setDate(from.getDate() - 7);
    return {
      from: beginningOfDay(from),
      to: earliestDate(endOfDay(to), roundedDatetimeToPastTenMins(to)),
      period: 'day',
    };
  });
  const { data } = useQueryHandler(DASHBOARD_PERMISSIONS);

  const {
    turnoverOverTime,
    persistedSessionsOverTime,
    countries,
    pointsOfSale,
    products,
    averageOrderValueOverTime,
    placedOrderCountOverTime,
  } = data?.statistics?.permissions || {};

  const anyStatistics =
    data &&
    some([
      turnoverOverTime,
      persistedSessionsOverTime,
      countries,
      pointsOfSale,
      products,
      averageOrderValueOverTime,
      placedOrderCountOverTime,
    ]);
  const noStatistics = data && !anyStatistics;

  const isMerchActivated = merchActivated(membership);
  const isTicketingActivated = ticketingActivated(membership);

  return (
    <NewContainer>
      <Stack size="lg">
        <Stack line justify="space-between" wrap size="16">
          <Text type="page-title-bold">{t.page.dashboard.title}</Text>
          {anyStatistics && (
            <IntervalField
              dashboard
              disableFutureDates
              initialValue={intervalValues}
              value={{ from: intervalValues.from, to: intervalValues.to, period: 'day' }}
              onChange={(v) => {
                if (v) {
                  setIntervalValues((prev) => ({
                    from: v.from || prev.from,
                    to: roundedDatetimeToPastTenMins(v.to || prev.to),
                    period: v.period || prev.period,
                  }));
                }
              }}
              position="bottom-end"
            />
          )}
        </Stack>
        <Stack size="xl">
          {noStatistics && !isMerchActivated && !isTicketingActivated && (
            <NoDataDisplay message={t.page.dashboard.noDataToShow} />
          )}

          {noStatistics && (isMerchActivated || isTicketingActivated) && (
            <Grid>
              {isMerchActivated && (
                <Card title={t.model.product._plural}>
                  <NavLink to={productsPath()} className="button button--primary">
                    {t.actions.view(t.model.product._plural)}
                  </NavLink>
                </Card>
              )}
              {isTicketingActivated && (
                <Card title={t.model.event._plural}>
                  <NavLink to={eventsPath()} className="button button--primary">
                    {t.actions.view(t.model.event._plural)}
                  </NavLink>
                </Card>
              )}
            </Grid>
          )}
          {turnoverOverTime && <TurnoverOverTime {...intervalValues} />}
          {(placedOrderCountOverTime || averageOrderValueOverTime) && (
            <Grid gap="xl">
              {placedOrderCountOverTime && <OrdersPlaced {...intervalValues} />}
              {averageOrderValueOverTime && <AverageOrderValue {...intervalValues} />}
            </Grid>
          )}
          {products && <Products {...intervalValues} />}
          {persistedSessionsOverTime && <Sessions {...intervalValues} />}
          {pointsOfSale && <PointsOfSale {...intervalValues} />}
          {countries && <Countries from={intervalValues.from} to={intervalValues.to} period={intervalValues.period} />}
        </Stack>
      </Stack>
    </NewContainer>
  );
}
